import React, { ChangeEvent, FC, useCallback, useMemo } from 'react'
import { $enum } from 'ts-enum-util'
import { ILoadStatusFunctionalTagProps } from 'apps/fleet-planner/components/atoms/LoadStatusFunctionalTag'
import { LOAD_STATE } from 'apps/fleet-planner/constants/loads'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import {
  filterPickupStops,
  filterDropoffStops,
  getOptions,
  getLoadStatusTagStyle,
  getLoadStatusOptionValue,
} from 'apps/fleet-planner/utils/loadStatusTags'
import { IFunctionalTagOption, TagSelect, TAG_SIZE } from 'transfix-ui'

interface ILoadStatusFunctionalTagPropsMobile extends ILoadStatusFunctionalTagProps {
  /**
   * @default 'medium'
   */
  size?: keyof typeof TAG_SIZE
}

export const LoadStatusFunctionalTagMobile: FC<ILoadStatusFunctionalTagPropsMobile> = ({
  status,
  currentStopSequence = 0,
  onChange,
  stops = [],
  dataTest = 'load-status-mobile',
  isLoading,
  size = TAG_SIZE.medium,
}) => {
  const { trackEvent } = useFMSAnalytics()
  const pickupStops = useMemo(() => filterPickupStops(stops), [stops])
  const dropoffStops = useMemo(() => filterDropoffStops(stops), [stops])
  const options = useMemo(() => getOptions({ pickupStops, dropoffStops }), [
    pickupStops,
    dropoffStops,
  ])

  const statusKey = $enum(LOAD_STATE).getKeyOrDefault(status, 'Pending')
  const statusTagStyle = getLoadStatusTagStyle(statusKey)

  const updateStatusHandler = useCallback(
    (selectedOption: IFunctionalTagOption<string>) => {
      const [, updatedStatus, stopSequence] = selectedOption.value.split(':') as [
        string,
        LOAD_STATE,
        string
      ]
      onChange({ status: updatedStatus, currentStopSequence: stopSequence })
      trackEvent('Load', 'CHANGED_LOAD_STATUS')
    },
    [onChange]
  )

  const changeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedOption = options.find(e => e?.value === event?.target?.value)
    if (selectedOption) updateStatusHandler(selectedOption)
  }

  const currentStatusValue = useMemo(
    () =>
      getLoadStatusOptionValue(
        $enum(LOAD_STATE).getKeyOrDefault(status, 'Pending'),
        currentStopSequence
      ),
    [status, currentStopSequence]
  )

  const value = options?.find(v => v?.value === currentStatusValue)

  return (
    <TagSelect
      options={options}
      size={TAG_SIZE[size]}
      tagStyle={statusTagStyle}
      outlineOnly={status === LOAD_STATE.Pending}
      onChange={changeHandler}
      value={value}
      disabled={isLoading}
      dataTest={dataTest}
    />
  )
}
